import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react'

export default function WhoopCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('processing') // processing, success, error
  const [error, setError] = useState('')

  useEffect(() => {
    handleCallback()
  }, [])

  async function handleCallback() {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const errorParam = searchParams.get('error')

    if (errorParam) {
      setStatus('error')
      setError(searchParams.get('error_description') || 'Authorization was denied')
      return
    }

    if (!code) {
      setStatus('error')
      setError('No authorization code received')
      return
    }

    try {
      const res = await fetch('/api/auth/whoop/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state })
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.message || 'Failed to connect Whoop')
      }

      setStatus('success')
      
      // Redirect to dashboard after brief success message
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 2000)
    } catch (err) {
      setStatus('error')
      setError(err.message)
    }
  }

  return (
    <div className="page" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh'
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card-static"
        style={{ 
          padding: 'var(--space-2xl)',
          textAlign: 'center',
          maxWidth: 400
        }}
      >
        {status === 'processing' && (
          <>
            <Loader2 
              size={48} 
              className="animate-spin" 
              style={{ 
                color: 'var(--accent-blue)',
                margin: '0 auto var(--space-lg)'
              }} 
            />
            <h3 style={{ marginBottom: 'var(--space-sm)' }}>Connecting Whoop...</h3>
            <p className="text-secondary">Please wait while we complete the connection.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
            >
              <CheckCircle2 
                size={48} 
                style={{ 
                  color: 'var(--accent-green)',
                  margin: '0 auto var(--space-lg)'
                }} 
              />
            </motion.div>
            <h3 style={{ marginBottom: 'var(--space-sm)' }}>Whoop Connected!</h3>
            <p className="text-secondary">
              Your wellness data will now power personalized recommendations.
            </p>
            <p className="text-tertiary" style={{ 
              marginTop: 'var(--space-lg)',
              fontSize: '0.875rem'
            }}>
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle 
              size={48} 
              style={{ 
                color: 'var(--accent-red)',
                margin: '0 auto var(--space-lg)'
              }} 
            />
            <h3 style={{ marginBottom: 'var(--space-sm)' }}>Connection Failed</h3>
            <p className="text-secondary" style={{ marginBottom: 'var(--space-lg)' }}>
              {error}
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/settings')}
            >
              Back to Settings
            </button>
          </>
        )}
      </motion.div>
    </div>
  )
}
